import { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, Building2, UserSquare2, Users, Mail, FileBarChart2, Settings, LogOut, Menu, X, ChevronLeft, Home
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { Lock } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_toledo-realestate/artifacts/oy9vxwiq_01.png';

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const navItems = [
    { href: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { href: '/admin/properties', label: 'Imóveis', icon: Building2 },
    { href: '/admin/owners', label: 'Proprietários', icon: UserSquare2 },
    { href: '/admin/clients', label: 'Clientes', icon: Users },
    { href: '/admin/leads', label: 'Leads', icon: Mail },
    { href: '/admin/reports', label: 'Relatórios', icon: FileBarChart2 },
  ];

  const handleLogout = () => {
  logout();
  window.location.href = '/admin/login';
  };

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2">
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <img src={LOGO_URL} alt="Logo" className="h-20 w-auto" />
        <div className="w-10" />
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 h-full bg-white border-r border-slate-200 z-50 transition-all duration-300",
        sidebarOpen ? "w-72" : "w-20",
        "hidden lg:block",
        mobileMenuOpen && "block w-72"
      )}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200">
          {sidebarOpen && (
            <img src={LOGO_URL} alt="Logo" className="h-24 w-auto" />
          )}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex p-2 rounded-lg hover:bg-slate-100"
          >
            <ChevronLeft className={cn("w-5 h-5 transition-transform", !sidebarOpen && "rotate-180")} />
          </button>
        </div>

        {/* Nav Items */}
        <nav className="p-4 space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setMobileMenuOpen(false)}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                isActive(item.href) 
                  ? "bg-brand-blue text-white" 
                  : "text-slate-600 hover:bg-slate-100",
                !sidebarOpen && "justify-center"
              )}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="font-medium">{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <Link
            to="/"
            target="_blank"
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100 mb-2",
              !sidebarOpen && "justify-center"
            )}
          >
            <Home className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm">Ver Site</span>}
          </Link>
          
          {sidebarOpen && user && (
            <div className="px-3 py-2 mb-2 space-y-2">
              <div>
                <p className="text-sm font-medium text-slate-800 truncate">{user.name}</p>
                <p className="text-xs text-slate-500 truncate">{user.email}</p>
              </div>

              <Link
                to="/admin/change-password"
                className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-slate-100 text-slate-600 hover:text-brand-blue text-sm transition-colors"
              >
                <Lock className="w-4 h-4" />
                <span>Alterar senha</span>
              </Link>
            </div>
          )}
          
          <button
            onClick={handleLogout}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-500 hover:bg-red-50 w-full",
              !sidebarOpen && "justify-center"
            )}
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm">Sair</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn(
        "min-h-screen transition-all duration-300 pt-16 lg:pt-0",
        sidebarOpen ? "lg:ml-72" : "lg:ml-20"
      )}>
        <div className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
